/**
 * `PS.PAL`'s top 32 entries — the shell's UI ramp, shared by every menu
 * screen. Indices 224-242 are a light-to-dark warm tan ramp, 243-254 a
 * dark-to-light burnt-orange ramp, 255 mid grey. The ui-reference lane
 * confirmed DOSBox-X's VGA output hits these entries at squared-distance 0
 * (docs/game/ui-reference/index.md), and that every screen palette
 * (`MAINMENU.PAL`, …) carries the identical 224-255 block, so drawing menu
 * chrome by index is exact rather than approximate.
 */

export interface IndexedPalette {
  readonly colors: readonly (readonly [number, number, number])[];
}

/** Lightest tan — top bevel highlights, button caps, the bar ornaments below the board. */
export const TAN_LIGHTEST = 224;
export const TAN_226 = 226;
export const TAN_FACE = 228;
export const TAN_231 = 231;
export const TAN_232 = 232;
export const TAN_233 = 233;
export const TAN_234 = 234;
export const TAN_236 = 236;
/** Darkest orange — the engraved top edge of a button label's strokes. */
export const ORANGE_DARKEST = 243;
export const ORANGE_245 = 245;
export const ORANGE_LABEL = 246;
export const ORANGE_247 = 247;
export const ORANGE_PLATE = 249;
export const ORANGE_251 = 251;
export const ORANGE_HIGHLIGHT = 252;
export const ORANGE_LIGHTEST = 254;

export const PALETTE_PATH = "/baked/palettes/PS.PAL.json";

export function palettePath(name: string): string {
  return `/baked/palettes/${name}.json`;
}

export async function loadPalette(name: string): Promise<IndexedPalette> {
  const path = palettePath(name);
  const response = await fetch(path);
  if (!response.ok) throw new Error(`shell: ${path} → ${response.status} ${response.statusText}`);
  const parsed = (await response.json()) as { colors?: unknown };
  const colors = Array.isArray(parsed.colors) ? (parsed.colors as (readonly [number, number, number])[]) : [];
  if (colors.length < 256) throw new Error(`shell: ${path} has ${colors.length} colors, expected 256`);
  return { colors };
}

/**
 * The shell's UI ramp, fetched once per document.
 *
 * Memoised because it now has two callers that run *at the same moment*:
 * `/fly`'s loading screen wants the ramp on its own so it can draw its plate and
 * bar before the bake manifest arrives, and `loadShellFonts` wants it as part of
 * the font base — and both fire when `/fly` mounts. Only `PS.PAL` is cached, not
 * `loadPalette` in general: the cutscene stage loads a different `.PAL` per
 * scene and has no such duplication to remove.
 */
let sharedPsPalette: Promise<IndexedPalette> | undefined;

export function loadPsPalette(): Promise<IndexedPalette> {
  sharedPsPalette ??= loadPalette("PS.PAL");
  const pending = sharedPsPalette;
  // A failure must not be replayed forever — the next caller retries.
  pending.catch(() => {
    if (sharedPsPalette === pending) sharedPsPalette = undefined;
  });
  return pending;
}

/** Throws on an index the palette does not define: `loadPsPalette` guarantees 256 entries, so a miss is a bug in the caller, not a colour to guess. */
export function paletteRgb(palette: IndexedPalette, index: number): readonly [number, number, number] {
  const color = palette.colors[index];
  if (!color) throw new Error(`shell: palette index ${index} is outside the loaded ${palette.colors.length}-entry palette`);
  return color;
}

export function paletteCss(palette: IndexedPalette, index: number): string {
  const [r, g, b] = paletteRgb(palette, index);
  return `rgb(${r},${g},${b})`;
}
